import moment from 'moment'
import consts from '../consts'

export default {
    getSlotCancellationLimit(slot, cancelDelay) {
        return moment(slot.StartDate).subtract(cancelDelay, 'hours').format()
    },

    getAgeLimits(activity, slotRepeat) {
        let minAge = activity.MinAge
        let maxAge = activity.MaxAge
        let typeMinAge = activity.TypeMinAge
        let typeMaxAge = activity.TypeMaxAge
        let minAgeTolerance = activity.MinAgeToleranceMonths
        let maxAgeTolerance = activity.MaxAgeToleranceMonths

        // User slot repeat limits if defined
        if (slotRepeat && (slotRepeat.MinAge || slotRepeat.MaxAge)) {
            minAge = slotRepeat.MinAge
            typeMinAge = slotRepeat.AgeMinType
            minAgeTolerance = slotRepeat.MinAgeDelta
            maxAge = slotRepeat.MaxAge
            typeMaxAge = slotRepeat.AgeMaxType
            maxAgeTolerance = slotRepeat.MaxAgeDelta
        }

        return {
            MinAge: minAge, MaxAge: maxAge,
            TypeMinAge: typeMinAge, TypeMaxAge: typeMaxAge,
            MinAgeTolerance: minAgeTolerance, MaxAgeTolerance: maxAgeTolerance
        }
    },

    clientMatchAge(client, activity, slotRepeat, startDate) {
        if (client) {
            const limits = this.getAgeLimits(activity, slotRepeat)

            let minAgeMonth = limits.MinAge
            if (limits.TypeMinAge === 1) {
                minAgeMonth *= 12
            }

            let maxAgeMonth = limits.MaxAge
            if (limits.TypeMaxAge === 1) {
                maxAgeMonth *= 12
            }

            if (!client.BirthDate) {
                return limits.MinAge === 0 && limits.MaxAge === 0
            } else {
                // Redefine start date if in past or according to slot repeat option
                // If it's an already started periodic session we want to check against now instead of session start
                // It would be better to check against the next coming slot of the session, but itwould be a little overkill
                if (moment(startDate).isBefore(moment()) || (slotRepeat && slotRepeat.DeltaMode === consts.DELTA_MODE.CURRENT_YEAR)) {
                    startDate = moment().format()
                }

                const clientAgeInMonth = moment(startDate).diff(moment(client.BirthDate), 'months')

                if (limits.MinAge !== 0 && clientAgeInMonth < minAgeMonth - limits.MinAgeTolerance) {
                    return false
                }
                if (limits.MaxAge !== 0 && clientAgeInMonth > maxAgeMonth + limits.MaxAgeTolerance) {
                    return false
                }
            }
        }
        return true
    },

    clientMatchActivityMedicalCert(client, slot, certValidity = 3) {
        let validMedicert = true
        if (client) {
            let compareDate = moment()
            if (slot) {
                compareDate = moment(slot.StartDate)
            }

            if (slot.Activity && slot.Activity.IsMedicertRequired) {
                if (!!client.MedicertIssueDate) {
                    validMedicert = moment(client.MedicertIssueDate).add(certValidity, 'years').isAfter(compareDate)
                } else if (!!client.MedicertLimitDate) {
                    validMedicert = moment(client.MedicertLimitDate).isAfter(compareDate)
                }
            }
        }
        return validMedicert
    },

    getSlotAgeIntervalText(activity, slotRepeat) {
        const limits = this.getAgeLimits(activity, slotRepeat)
        let text = ''

        if (limits.MinAge === 0 && limits.MaxAge !== 0) {
            text = 'moins de ' + limits.MaxAge + ' ' + this.getSlotAgeUnit(limits.TypeMaxAge, limits.MaxAge)
            if (limits.MaxAgeTolerance) {
                text += ' (+/- ' + limits.MaxAgeTolerance + ' mois)'
            }
        } else if (limits.MinAge !== 0 && limits.MaxAge === 0) {
            text = 'plus de ' + limits.MinAge + ' ' + this.getSlotAgeUnit(limits.TypeMinAge, limits.MinAge)
            if (limits.MinAgeTolerance) {
                text += ' (+/- ' + limits.MinAgeTolerance + ' mois)'
            }
        } else if (limits.MinAge !== 0 && limits.MaxAge !== 0) {
            text = 'entre ' + limits.MinAge + ' ' + this.getSlotAgeUnit(limits.TypeMinAge, limits.MinAge)
            if (limits.MinAgeTolerance) {
                text += ' (+/- ' + limits.MinAgeTolerance + ' mois)'
            }
            text += ' et ' + limits.MaxAge + ' ' + this.getSlotAgeUnit(limits.TypeMaxAge, limits.MaxAge)
            if (limits.MaxAgeTolerance) {
                text += ' (+/- ' + limits.MaxAgeTolerance + ' mois)'
            }
        }

        return text
    },

    getSlotAgeUnit(type, value) {
        if (type === 1) {
            return value > 1 ? 'ans' : 'an'
        } else {
            return 'mois'
        }
    },

    getSlotDebitForProduct(slot, product) {
        let debit = 1
        if (slot.Debit > -1) {
            debit = slot.Debit
        } else if (product) {
            let productDebit = null
            productDebit = slot.Activity && slot.Activity.ProductsDebit && slot.Activity.ProductsDebit.find(p => p.ProductID === product.ID)

            if (productDebit && productDebit.Debit) {
                debit = productDebit.Debit
            } else {
                debit = slot.Activity.DefaultDebit
            }
        }
        return debit
    },

    getMaxBookablePlaces(slot, product, placesAvailable, subscriptionQuantity, clientExistingBookings = []) {
        // Take the min limit between activity, slot and product, and of course client subscription quantity
        let limit = 1
        let debited = this.getSlotDebitForProduct(slot, product)

        if (slot.Activity && slot.Activity.AllowMultipleBookings) {
            limit = slot.Activity.MultipleBookingsLimit
        } else {
            limit = 1
        }

        if (slot.Debit !== -1) {
            debited = slot.Debit
        }

        if (slot.AllowMultipleBookings) {
            if (limit > slot.MultipleBookingsLimit) {
                limit = slot.MultipleBookingsLimit
            }
        } else {
            limit = 1
        }

        const debitable = Math.floor(subscriptionQuantity / debited)

        // Adjust with bookings client might already have made for slot
        if (clientExistingBookings && clientExistingBookings.length) {
            if ((slot.Activity && !slot.Activity.AllowMultipleProducts) || clientExistingBookings.some(b => b.ProductID === product.ID)) {
                return 0 // Already booked
            }

            const placesAlreadyBookedForSlot = clientExistingBookings.reduce((acc, cur) => {
                const participantsCount = cur.ParticipantsCount || 0
                acc += participantsCount
                return acc
            }, 0)
            limit -= placesAlreadyBookedForSlot
        }

        // Check product limit and subscription quantity
        if (product) {
            if (product.ID === consts.FAKE_PRODUCT_UNITS && limit > debitable) {
                limit = debitable
            } else if (product.Subscription) {
                if (product.Subscription.AllowMultipleBookings) {
                    if (limit > product.Subscription.MultipleBookingsLimit) {
                        limit = product.Subscription.MultipleBookingsLimit
                    }
                } else {
                    limit = 1
                }

                if (product.Subscription.Type === consts.SUBSCRIPTION_TYPE_PASS_Q && limit > debitable) {
                    limit = debitable
                } else if (product.Subscription.Type === consts.SUBSCRIPTION_TYPE_SUBSCRIPTION) {
                    limit = 1
                }
            }
        }

        // Adjust with bookings client might already have made for slot with same product
        const placesAlreadyBookedWithProduct = clientExistingBookings.reduce((acc, cur) => {
            if (cur.ProductID === product.ID) {
                const participantsCount = cur.ParticipantsCount || 1
                acc += participantsCount
            }
            return acc
        }, 0)
        limit -= placesAlreadyBookedWithProduct

        return Math.min(limit, placesAvailable)
    },

    getBookingsLimits(slot, product, placesAvailable, subscriptionQuantity, clientExistingBookings = []) {
        const items = []

        // Take the min limit between activity, slot and product, and of course client subscription quantity
        let limit = this.getMaxBookablePlaces(slot, product, placesAvailable, subscriptionQuantity, clientExistingBookings)

        // Check quantity debited for slot
        let debit = this.getSlotDebitForProduct(slot, product)

        if (limit > 1) {
            let personCount = 1
            if (product && product.Subscription && product.Subscription.PersonCount > 1) {
                personCount = product.Subscription.PersonCount
            }

            for (let participants = personCount; participants <= limit; participants += personCount) {
                const quantity = participants / personCount
                const debited = quantity * debit
                if (participants <= placesAvailable) {
                    items.push({
                        text: (participants + ' personne' + (participants > 1 ? 's' : '') + ' (Quantité débitée : ' + debited + ')'),
                        value: quantity,
                        personCount,
                        participants,
                        debited
                    })
                }
            }
        }

        return items
    }
}